import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Dialog from "../Common/Dailog/Dailog";
import env from "../../env";
import customConfig from "../../config";

/** sessionStorage key — keep value stable so existing dismissals still apply */
export const MFA_REMINDER_DISMISSED_KEY = "mfaSetupPromptDismissed";

/**
 * Soft MFA reminder dialog (Option 1): after login landing, if MFA is not enabled,
 * ask the user to set it up. Matches the Option 1 mockup (shield + soft dialog).
 */
const MfaReminderDialog = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const requestedRef = useRef(false);

  useEffect(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;

    if (
      typeof sessionStorage !== "undefined" &&
      sessionStorage.getItem(MFA_REMINDER_DISMISSED_KEY) === "1"
    ) {
      return;
    }

    let cancelled = false;

    const checkMfaStatus = async () => {
      try {
        const iamURL = env.GET_IAM_API_URL();
        const tenantId = env.GET_TENANT_ID();
        const response = await axios.get(`${iamURL}/api/identity/v1/user/profile`, {
          headers: {
            "Content-Type": "application/json",
            Tenantid: tenantId,
          },
        });
        if (cancelled) return;
        if (!Boolean(response?.data?.mfaEnabled)) {
          setOpen(true);
        }
      } catch {
        // Do not block landing if profile check fails.
      }
    };

    checkMfaStatus();
    return () => {
      cancelled = true;
    };
  }, []);

  const dismissForSession = () => {
    try {
      sessionStorage.setItem(MFA_REMINDER_DISMISSED_KEY, "1");
    } catch {
      // ignore storage errors
    }
    setOpen(false);
  };

  const handleRemindLater = () => {
    dismissForSession();
  };

  const handleSetupNow = () => {
    dismissForSession();
    navigate("/settings");
  };

  return (
    <Dialog
      open={open}
      onClose={handleRemindLater}
      maxWidth="xs"
      fullWidth
      closeOnBackdropClick
      closeOnEscapeKeyDown
      contentSx={{ pt: 3, pb: 1 }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          px: 0.5,
        },
      }}
      sx={{
        "& .MuiDialogActions-root": {
          justifyContent: "center",
          gap: 1.5,
          px: 3,
          pb: 3,
          pt: 1,
        },
      }}
      actions={[
        {
          label: customConfig.mfaSetupPromptLater,
          variant: "ghost",
          onClick: handleRemindLater,
        },
        {
          label: customConfig.mfaSetupPromptSetupNow,
          variant: "primary",
          onClick: handleSetupNow,
        },
      ]}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: 1.5,
          px: 1,
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: 72,
            height: 72,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 0.5,
          }}
          aria-hidden="true"
        >
          <ShieldOutlinedIcon
            sx={{
              fontSize: 72,
              color: "primary.main",
            }}
          />
          <LockOutlinedIcon
            sx={{
              position: "absolute",
              fontSize: 28,
              color: "primary.main",
              top: "52%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          />
        </Box>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: "text.primary",
            lineHeight: 1.3,
          }}
        >
          {customConfig.mfaSetupPromptTitle}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 320, lineHeight: 1.5 }}
        >
          {customConfig.mfaSetupPromptMessage}
        </Typography>
      </Box>
    </Dialog>
  );
};

export default MfaReminderDialog;
